import {
  ProfileTheme_1Icon,
  ProfileTheme_2Icon,
  ProfileTheme_3Icon,
  ProfileTheme_4Icon,
  ProfileTheme_5Icon,
} from "@dataone/icons";
import {
  ICurrentUser,
  openNotification,
  updateUser,
  dataRole,
  dataCompany,
} from "@dataone/lib";
import { useMutation } from "@tanstack/react-query";
import { Button, Form, Input, Select } from "antd";
import { useState } from "react";
import { styled } from "styled-components";
import { useAuth } from "../../auth/contexts/auth.provider";

interface ProfileInformationProps {
  children?: React.ReactNode;
  // user: ICurrentUser;
}

interface IUpdateProfileForm {
  firstName: string;
  avatar: string;
  lastName: string;
  email: string;
  role: string;
  companyType: string;
  companySize: string;
}

const ProfileInformation = ({}: ProfileInformationProps) => {
  const { user } = useAuth();
  const [profileForm] = Form.useForm();
  const themes = ["1", "2", "3", "4", "5"];
  const [selectedAvatar, setSelectedAvatar] = useState<string>(
    user?.avatar || "1"
  );
  const { mutate: editProfile, isLoading } = useMutation(updateUser);
  const { getCurrentUser } = useAuth();

  const handleAvatarChange = (key: string) => {
    setSelectedAvatar(key);
  };

  const handeleCancel = () => {
    profileForm.resetFields();
    getCurrentUser();
  };

  const handleFormConfirm = (values: IUpdateProfileForm) => {
    if (user)
      editProfile(
        {
          ...values,
          company: {
            role: values.role,
            companyType: values.companyType,
            companySize: user?.company?.companySize ?? "",
          },
          avatar: selectedAvatar,
        },
        {
          onSuccess: () => {
            openNotification("Profile updated successfully");
            getCurrentUser();
          },
        }
      );
  };

  interface ProfileIcons {
    [key: string]: JSX.Element;
  }

  const profileIcons: ProfileIcons = {
    "1": <ProfileTheme_1Icon />,
    "2": <ProfileTheme_2Icon />,
    "3": <ProfileTheme_3Icon />,
    "4": <ProfileTheme_4Icon />,
    "5": <ProfileTheme_5Icon />,
  };
  return (
    <StyledProfileInformation>
      <h2>My Profile</h2>
      <Form
        layout="vertical"
        form={profileForm}
        onFinish={handleFormConfirm}
        {...(user && { initialValues: user })}
      >
        <Form.Item
          style={{
            marginBottom: 0,
          }}
        >
          <Form.Item
            name="firstName"
            rules={[
              {
                whitespace: true,
                required: true,
                message: "Please input your first name",
              },
            ]}
            style={{
              display: "inline-block",
              width: "calc(50% - 8px)",
            }}
            label={<StyledLabel>First Name</StyledLabel>}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item
            name="lastName"
            rules={[
              {
                required: true,
                message: "Please input your last name",
              },
            ]}
            style={{
              display: "inline-block",
              width: "calc(50% - 8px)",
              margin: "0 8px",
            }}
            label={<StyledLabel>Last Name</StyledLabel>}
          >
            <Input maxLength={255} />
          </Form.Item>
        </Form.Item>
        <Form.Item
          name="email"
          rules={[
            {
              required: true,
              message: "Please input your email",
            },
          ]}
          style={{
            display: "inline-block",
            width: "calc(100% - 8px)",
          }}
          label={<StyledLabel>Email</StyledLabel>}
        >
          <Input disabled maxLength={255} />
        </Form.Item>
        <Form.Item
          name="role"
          style={{
            display: "inline-block",
            width: "calc(50% - 8px)",
          }}
          label={<StyledLabel>Your role</StyledLabel>}
        >
          <Select
            allowClear
            defaultValue={user?.company?.role || null}
            options={dataRole.map((v) => ({ value: v, label: v }))}
            maxLength={255}
            variant="borderless"
            placeholder="What best describe your role ?"
          />
        </Form.Item>
        <Form.Item
          name="companyType"
          style={{
            display: "inline-block",
            width: "calc(50% - 8px)",
            margin: "0 8px",
          }}
          label={<StyledLabel>Your company</StyledLabel>}
        >
          <Select
            allowClear
            defaultValue={user?.company?.companyType || null}
            options={dataCompany.map((v) => ({ value: v, label: v }))}
            maxLength={255}
            variant="borderless"
            placeholder="Whish industry best describe your company ?"
          />
        </Form.Item>
        <StyledLabel>
          <span className="span">*</span> Avatar
        </StyledLabel>
        <StyledThemeContainer>
          <div className="theme-icons">
            {themes.map((theme) => {
              return (
                <Button
                  key={theme}
                  onClick={() => handleAvatarChange(theme)}
                  type="link"
                  className={
                    selectedAvatar === theme
                      ? "btn-theme btn-selected"
                      : "btn-theme"
                  }
                  icon={profileIcons[theme as keyof ProfileIcons]}
                />
              );
            })}
          </div>
        </StyledThemeContainer>
      </Form>
      <StyledActions>
        <div className="btn">
          <Button className="cancel" key="back" onClick={handeleCancel}>
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            onClick={() => profileForm.submit()}
            disabled={isLoading}
          >
            {isLoading ? "Saving ..." : "Save"}
          </Button>
        </div>
      </StyledActions>
    </StyledProfileInformation>
  );
};

export default ProfileInformation;

const StyledProfileInformation = styled.div`
  width: 100%;
  padding: 15px 0px 0px 30px;
  .ant-form-item-explain-error {
    font-size: 12px !important;
  }
  h2 {
    color: #4974f6;
    font-weight: 700;
    font-size: 18px;
    margin-top: 2px;
    margin-bottom: 40px;
  }

  .ant-btn-default {
    border-radius: 6px;
    background: #eff1fd;
    border: 0;
    font-size: 12px;
    font-weight:600 !important;

    &:hover {
      color: #040726 !important;
        rgba(0, 0, 0, 0.25) inset;
      opacity: 0.8;
    }
  }

  .ant-btn-primary {
    border-radius: 6px;
    color: #fff;
    background: #4974f6 !important;
    font-size: 12px;
    font-weight:600 !important;
  }

  .ant-modal-content {
    width: 450dvh;
    padding: 15px 35px;
  }

  .ant-modal-close-x .anticon svg {
    margin-right: 40px;
    margin-top: 8px;
  }

  span.anticon.anticon-close.ant-modal-close-icon {
    background-color: white;
  }


  .ant-select-selector {
    background: #eff1fd !important;
  }

  .ant-select-single {
    height: 40px;
  }

  .ant-input {
    display: flex;
    margin-inline: auto;
    border-radius: 6px;
    background: #eff1fd !important;
    border: none;
    box-shadow: 0px 4px 4px 0px #fff;
    font-size: 12px;
    color: #000;
    &:is(input) {
      height: 40px;
    }

    &:is(textarea) {
      height: 131px;
    }
  }

  .react-tel-input .form-control {
    height: 40px !important;
    width: 100%;
    border-radius: 10px;
    background: #eff1fd;
    height: 40px;
    border: none;
    padding-left: 80px;
  }
  .react-tel-input .flag-dropdown {
    border-radius: 11px 0 0 11px !important;
    background-color: #eff1fd !important;
    border: none !important;
    margin: 10px;
  }
  .react-tel-input .selected-flag {
    width: 60px;
    border-right: 1px solid #828d9c !important;
  }
  .react-tel-input .selected-flag .arrow {
    left: 30px !important;
  }
`;

const StyledThemeContainer = styled.div`
  text-align: center;

  .theme-icons {
    margin: auto 0;
    text-align: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    align-items: center;
    gap: 2%;

    &:nth-child(1) {
      margin-top: 20px;
    }
  }

  .btn-theme {
    height: 50px;
    margin-right: 35px;
    margin-bottom: 15px;
    padding: 0;
    cursor: pointer;
    svg {
      border-radius: 50%;
      font-size: 50px;
      width: 50px;
      border: 3px solid #fff;
    }
    &.btn-selected svg {
      border: 3px solid #fff;
      outline: 2px solid #4974f6;
    }
  }
`;

const StyledLabel = styled.div`
  color: #040726;
  font-size: 13px;
  font-weight: bold;
  letter-spacing: -0.27px;

  span {
    color: red;
    font-family: SimSun, sans-serif;
  }
`;

const StyledActions = styled.div`
  display: flex;
  margin-right: 8px;
  .btn {
    display: flex;
    margin-left: auto;
    margin-top: 30px;
  }
  .cancel {
    margin-right: 10px;
  }
`;
